import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Banknote, Loader2, Search } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const METHODS = [
  { id: 'cash', label: 'Cash' },
  { id: 'cashapp', label: 'Cash App' },
  { id: 'zelle', label: 'Zelle' },
  { id: 'card', label: 'Card' },
];

const SplitForm = ({ row, paidOn, onSplit, busy }) => {
  const [amts, setAmts] = useState({});
  const total = METHODS.reduce((s, m) => s + (parseFloat(amts[m.id]) || 0), 0);
  const left = Math.round((row.balance - total) * 100) / 100;
  return (
    <div data-testid={`balance-split-form-${row.booking_number}`} className="w-full flex flex-wrap items-center gap-2 pt-3 border-t border-neutral-800">
      {METHODS.map((m) => (
        <label key={m.id} className="flex items-center gap-1 text-xs text-neutral-400">
          {m.label}
          <input
            type="number" min="0" step="0.01" inputMode="decimal" placeholder="0"
            value={amts[m.id] || ''}
            onChange={(e) => setAmts((a) => ({ ...a, [m.id]: e.target.value }))}
            data-testid={`balance-split-${m.id}-${row.booking_number}`}
            className="w-20 bg-black border border-neutral-700 rounded-lg px-2 py-1 text-sm text-white"
          />
        </label>
      ))}
      <span data-testid={`balance-split-left-${row.booking_number}`} className={`text-xs ${left === 0 ? 'text-green-400' : 'text-amber-400'}`}>
        {left === 0 ? 'Adds up' : `$${left.toFixed(2)} ${left > 0 ? 'left' : 'over'}`}
      </span>
      <button
        disabled={busy || left !== 0}
        onClick={() => onSplit(row, METHODS.filter((m) => parseFloat(amts[m.id]) > 0).map((m) => ({ method: m.id, amount: parseFloat(amts[m.id]) })), paidOn)}
        data-testid={`balance-split-save-${row.booking_number}`}
        className="ml-auto px-3 py-1.5 rounded-full text-xs font-semibold bg-purple-500 text-white hover:bg-purple-400 disabled:opacity-40 transition-colors duration-200"
      >
        Save split
      </button>
    </div>
  );
};

const BalanceRow = ({ row, onPaid, onSplit, busy }) => {
  const [paidOn, setPaidOn] = useState(row.date || '');
  const [splitOpen, setSplitOpen] = useState(false);
  return (
    <div data-testid={`balance-row-${row.booking_number}`} className="p-4 rounded-xl bg-neutral-950 border border-neutral-800 flex flex-col lg:flex-row lg:flex-wrap lg:items-center gap-3">
      <div className="flex-1 min-w-0">
        <p className="text-white font-semibold truncate">{row.name || 'Client'} <span className="text-neutral-500 text-xs font-normal">{row.booking_number}</span></p>
        <p className="text-neutral-400 text-sm">{row.date} · {row.time} · {row.engineer_name}{row.wave_invoice_number ? ` · Wave #${row.wave_invoice_number}` : ''}</p>
      </div>
      <div className="text-right lg:w-28">
        <p className="text-purple-300 font-bold text-lg" data-testid={`balance-amount-${row.booking_number}`}>${row.balance.toFixed(2)}</p>
        <p className="text-neutral-500 text-[11px]">of ${Number(row.price).toFixed(2)}</p>
      </div>
      <input
        type="date"
        value={paidOn}
        onChange={(e) => setPaidOn(e.target.value)}
        data-testid={`balance-date-${row.booking_number}`}
        className="bg-black border border-neutral-700 rounded-lg px-2 py-1.5 text-sm text-white"
      />
      <div className="flex gap-1.5 flex-wrap">
        {METHODS.map((m) => (
          <button
            key={m.id}
            disabled={busy}
            onClick={() => onPaid(row, m.id, paidOn)}
            data-testid={`balance-pay-${m.id}-${row.booking_number}`}
            className="px-3 py-1.5 rounded-full text-xs font-semibold bg-purple-500/15 text-purple-200 border border-purple-500/30 hover:bg-purple-500/30 disabled:opacity-40 transition-colors duration-200"
          >
            {m.label}
          </button>
        ))}
        <button
          disabled={busy}
          onClick={() => setSplitOpen((o) => !o)}
          data-testid={`balance-split-toggle-${row.booking_number}`}
          className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors duration-200 ${splitOpen ? 'bg-white text-black border-white' : 'text-neutral-300 border-neutral-600 hover:border-neutral-400'}`}
        >
          Split
        </button>
      </div>
      {splitOpen && <SplitForm row={row} paidOn={paidOn} onSplit={onSplit} busy={busy} />}
    </div>
  );
};

const DRAWER_LABELS = { cash: 'Cash', cashapp: 'Cash App', zelle: 'Zelle', card: 'Card' };

const CashDrawer = ({ adminSecret, refreshKey }) => {
  const [d, setD] = useState(null);
  useEffect(() => {
    axios.get(`${API}/balances/drawer`, { params: { admin_secret: adminSecret } }).then((r) => setD(r.data)).catch(() => {});
  }, [adminSecret, refreshKey]);
  if (!d) return null;
  return (
    <div data-testid="cash-drawer" className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5">
      {[['today', 'Today'], ['week', 'This week'], ['month', 'This month']].map(([k, label]) => (
        <div key={k} data-testid={`cash-drawer-${k}`} className="rounded-xl border border-purple-500/25 bg-purple-500/5 p-4">
          <p className="text-xs uppercase tracking-wider text-neutral-400">{label} in studio</p>
          <p className="text-2xl font-bold text-white mt-1">${d[k].total.toFixed(2)}</p>
          <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-0.5 text-xs">
            {Object.entries(DRAWER_LABELS).map(([m, l]) => (
              <span key={m} className="flex justify-between text-neutral-400">{l}<b className="text-neutral-200 font-medium">${d[k][m].toFixed(0)}</b></span>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

// auth: { admin_secret } for admins, { token } for engineers
export const BalancesPanel = ({ auth, title = 'Open studio balances' }) => {
  const [data, setData] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [query, setQuery] = useState('');
  const [paidCount, setPaidCount] = useState(0);

  const authKey = JSON.stringify(auth);
  const load = useCallback(() => {
    axios.get(`${API}/balances`, { params: JSON.parse(authKey) })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(e.response?.data?.detail || 'Could not load balances'));
  }, [authKey]);

  useEffect(() => { load(); }, [load]);

  const removeRow = (row) => {
    setPaidCount((c) => c + 1);
    setData((d) => ({ ...d, count: d.count - 1, total: d.total - row.balance, balances: d.balances.filter((b) => b.id !== row.id) }));
  };

  const markSplit = async (row, parts, paidOn) => {
    setBusyId(row.id);
    try {
      const r = await axios.post(`${API}/balances/${row.id}/split`, { ...auth, parts, paid_on: paidOn });
      const waveOk = (r.data.parts || []).every((p) => p.wave_recorded);
      toast.success(`$${r.data.amount} split payment recorded${waveOk ? ' + Wave updated' : ''}`);
      removeRow(row);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not record split');
    } finally {
      setBusyId(null);
    }
  };

  const markPaid = async (row, method, paidOn) => {
    setBusyId(row.id);
    try {
      const r = await axios.post(`${API}/balances/${row.id}/paid`, { ...auth, method, paid_on: paidOn });
      toast.success(`$${r.data.amount} balance marked paid${r.data.wave?.recorded ? ' + recorded in Wave' : ''}`);
      removeRow(row);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not mark paid');
    } finally {
      setBusyId(null);
    }
  };

  const rows = (data?.balances || []).filter((b) => `${b.name} ${b.booking_number} ${b.engineer_name}`.toLowerCase().includes(query.toLowerCase()));

  return (
    <div data-testid="balances-panel" className="bg-black/40 border border-neutral-800 rounded-2xl p-5">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Banknote className="text-purple-400" size={20} />
          <h3 className="text-white font-bold text-lg">{title}</h3>
        </div>
        {data && (
          <p className="text-neutral-400 text-sm" data-testid="balances-summary">
            {data.count} open · <b className="text-white">${data.total.toFixed(2)}</b>
          </p>
        )}
      </div>
      {auth.admin_secret && <CashDrawer adminSecret={auth.admin_secret} refreshKey={paidCount} />}
      <p className="text-neutral-500 text-xs mb-4">Tap how the client paid the rest, or tap Split if they paid with more than one (e.g. $40 Cash App + $20 cash). It marks the booking paid and records it on the Wave invoice (Cash → Cash on Hand, Cash App → Cash App account, Zelle → Business Checking, Card → Stripe Account).</p>
      <div className="relative mb-4">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search client, booking # or engineer"
          data-testid="balances-search"
          className="w-full bg-black border border-neutral-800 rounded-full pl-9 pr-4 py-2 text-sm text-white"
        />
      </div>
      {!data ? (
        <Loader2 className="animate-spin text-purple-400" />
      ) : rows.length === 0 ? (
        <p className="text-neutral-400 text-sm" data-testid="balances-empty">No open balances.</p>
      ) : (
        <div className="space-y-2 max-h-[640px] overflow-y-auto pr-1">
          {rows.map((r) => <BalanceRow key={r.id} row={r} onPaid={markPaid} onSplit={markSplit} busy={busyId === r.id} />)}
        </div>
      )}
    </div>
  );
};
